import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import isAfter from 'date-fns/isAfter'
import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable } from 'mobx'
import { getValue } from 'mobx-async'

import { asyncDebounce, formatDate, parseISODate } from '@avantstay/backoffice-core'

import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { CreateBookingStore } from '@new/domains/bookingsTapeChart/stores/CreateBookingStore'
import { TapeChartBlockStore } from '@new/domains/bookingsTapeChart/stores/TapeChartBlockStore'

import { getPreBookingPriceTotal } from '@/services/arriere/modules/bookingPrices'

import { BaseStore, CreateTapeContextProps } from '../__types'
import { tapeChartCreateTapeStore } from '../internal'
import { TapeChartQuotesStore } from './TapeChartQuotesStore'
import { TapeChartStateStore } from './TapeChartStateStore'

export interface RangeData {
  id: string
  secondaryId?: string
  start: string
  end?: string
  minStay?: number
  price?: number
}

export class SelectRangeStore {
  constructor(
    private getStateStore: () => Partial<TapeChartStateStore>,
    private getFiltersStore: () => BaseStore['filterStore'],
    private getQuotesStore: () => TapeChartQuotesStore,
  ) {
    makeObservable<SelectRangeStore, 'selectedRange'>(this, {
      selectedRange: observable,
      tentativeEnd: observable,
      setSelectedRange: action.bound,
      hasRange: computed,
      range: computed,
      rangeTotalPrice: computed,
      createReservation: action.bound,
      dateCellClick: action.bound,
      clearSelectedRange: action.bound,
      clearTentativeDate: action.bound,
      minStay: computed,
      maybeSetTentativeDate: action.bound,
    })
  }

  get stateStore() {
    return this.getStateStore()
  }

  get filtersStore() {
    return this.getFiltersStore()
  }

  get quotesStore() {
    return this.getQuotesStore()
  }

  private selectedRange: RangeData | undefined
  tentativeEnd: string | undefined

  setSelectedRange(newRange?: RangeData) {
    this.selectedRange = newRange
    this.tentativeEnd = undefined
  }

  get hasRange() {
    return this.selectedRange?.start && this.selectedRange.end
  }

  get range(): RangeData | undefined {
    if (!this.selectedRange) {
      return undefined
    }
    return {
      ...this.selectedRange,
      minStay: this.minStay,
      price: this.hasRange ? getValue(this.rangeTotalPrice) : undefined,
    }
  }

  debouncedPreBookingPriceBreakdownTotal = asyncDebounce(getPreBookingPriceTotal, 300)

  get rangeTotalPrice(): Promise<number | undefined> {
    if (!this.selectedRange || !this.selectedRange.start || !this.tentativeEnd) {
      return Promise.resolve(undefined)
    }

    return this.debouncedPreBookingPriceBreakdownTotal({
      homeId: this.selectedRange.id,
      guests: 1,
      pets: 0,
      from: this.selectedRange.start,
      until: this.tentativeEnd,
    }).then(({ total }) => total.total)
  }

  getAvailability(id: string, date: string) {
    if (!this.stateStore.selectRangeProps) {
      return {
        isAllDaysInRangeAvailable: false,
        isAllDaysWithSingleNightAvailable: false,
        hasDifferentDates: false,
        differentDates: [],
        blockedDayIsAfterSelectedRangeStart: false,
      }
    }

    const { blockedDatesById, blockedDatesWithSingleNight } = this.stateStore.selectRangeProps
    const blockedDates = blockedDatesById[id]
    const isAllDaysInRangeAvailable =
      this.selectedRange?.start && blockedDates
        ? eachDayOfInterval({
            start: parseISODate(this.selectedRange.start),
            end: parseISODate(date),
          })
            .map(it => formatDate(it))
            .every(it => !blockedDates.includes(it))
        : true

    const blockedDaysWithSingleNight = blockedDatesWithSingleNight[id]
    const isAllDaysWithSingleNightAvailable =
      this.selectedRange?.start && blockedDaysWithSingleNight
        ? eachDayOfInterval({
            start: parseISODate(this.selectedRange.start),
            end: parseISODate(date),
          })
            .map(it => formatDate(it))
            .every(it => !blockedDates.includes(it))
        : true

    const hasDifferentDates = !isEqual(blockedDatesWithSingleNight, blockedDates)
    const differentDates =
      blockedDaysWithSingleNight?.filter(day => !blockedDates.includes(day)) || []
    const blockedDayIsAfterSelectedRangeStart =
      this.selectedRange?.start &&
      differentDates
        .filter(
          day =>
            day !== this.selectedRange!.start &&
            isAfter(parseISODate(day), parseISODate(this.selectedRange!.start)),
        )
        .some(day => isAfter(parseISODate(date), parseISODate(day)) || date === day)

    return {
      isAllDaysInRangeAvailable,
      isAllDaysWithSingleNightAvailable,
      hasDifferentDates,
      differentDates,
      blockedDayIsAfterSelectedRangeStart,
    }
  }

  private handleSelectDatesAction(tapeContext: CreateTapeContextProps) {
    const { selectionQuotesListingIds } = this.quotesStore
    const { availableDates, setAvailableDates } = this.filtersStore
    const {
      createTapeContext,
      itemAlreadyExists,
      setCreateTapeContext,
      setCreateTapeContextPeriod,
    } = tapeChartCreateTapeStore

    const isQuotesToolActive = !!selectionQuotesListingIds?.length
    const tapeIsAlreadySelected = itemAlreadyExists(tapeContext, createTapeContext)

    setCreateTapeContextPeriod(tapeContext.period)
    if (isQuotesToolActive && !availableDates) {
      setAvailableDates?.(tapeContext.period)

      if (!tapeIsAlreadySelected) {
        setCreateTapeContext([tapeContext])
      }
      return
    }

    // In case quotes tool is not active, push tapeContext to selected home
    setCreateTapeContext([tapeContext])
    if (tapeContext.home?.id || tapeContext?.hotelRoomTypeId) {
      this.quotesStore?.getListingData?.(tapeContext.home?.id)
      TapeChartBlockStore.toggleCreateTapeBlockModal = true
    }
  }

  createReservation() {
    if (!this.selectedRange || !this.selectedRange.end) {
      return
    }

    const { id, start, end, secondaryId } = this.selectedRange
    const homeInfo = this.stateStore.getListingInfoObject?.(id)

    BookingsTapeChartStore.setCurrentHomeId(homeInfo?.id)
    const listingInfo = homeInfo
      ? {
          id: homeInfo?.id,
          name: homeInfo?.name || '',
          regionName: homeInfo?.regionName || '',
        }
      : undefined

    const periodInfo = { fromDate: start, toDate: end }

    const tapeContext = {
      hotelRoomTypeId: secondaryId,
      home: listingInfo,
      period: periodInfo,
    }

    const createReservationObject = {
      hotelRoomTypeId: secondaryId,
      home: listingInfo,
      period: periodInfo,
      guestCount: {
        adults: 1,
        children: 0,
        infants: 0,
      },
    }

    CreateBookingStore.setCreateReservationObject(createReservationObject)
    TapeChartBlockStore.setTapeBlockObject(tapeContext)

    this.handleSelectDatesAction(tapeContext)
    this.clearSelectedRange()
  }

  dateCellClick(id: string, date: string, secondaryId?: string) {
    if (this.filtersStore.availableDates) {
      return
    }

    if (
      this.selectedRange !== undefined &&
      this.selectedRange.id === id &&
      isAfter(parseISODate(date), parseISODate(this.selectedRange.start))
    ) {
      // We are guaranteed to have a start date set here, so we need to figure out what to do
      // - if all dates are available between them, we're good, otherwise we're not
      const {
        blockedDayIsAfterSelectedRangeStart,
        differentDates,
        hasDifferentDates,
        isAllDaysInRangeAvailable,
        isAllDaysWithSingleNightAvailable,
      } = this.getAvailability(id, date)

      if (
        hasDifferentDates &&
        isAllDaysWithSingleNightAvailable &&
        (differentDates.includes(formatDate(date)) || blockedDayIsAfterSelectedRangeStart)
      ) {
        this.setSelectedRange({
          id,
          secondaryId,
          start: date,
        })

        return
      }

      // If they are available we just set it
      if (isAllDaysInRangeAvailable) {
        this.setSelectedRange({
          ...this.selectedRange,
          secondaryId,
          end: date,
        })

        setTimeout(this.createReservation, 500)
        return
      }
    }

    if (
      this.selectedRange &&
      this.selectedRange.id === id &&
      (this.selectedRange.start === date || this.selectedRange.end === date)
    ) {
      this.setSelectedRange(undefined)
    } else {
      this.setSelectedRange({
        id,
        secondaryId,
        start: date,
      })
    }
  }

  clearSelectedRange() {
    this.setSelectedRange(undefined)
  }

  clearTentativeDate(homeId: string) {
    if (this.selectedRange?.id === homeId) {
      this.tentativeEnd = undefined
    }
  }

  get minStay() {
    if (this.selectedRange?.start && this.stateStore.selectRangeProps?.datePrices) {
      const rangeStart = formatDate(this.selectedRange.start)
      const minStay = this.stateStore.selectRangeProps.datePrices.find(
        datePrice =>
          formatDate(datePrice.date) === rangeStart && datePrice.homeId === this.selectedRange?.id,
      )?.minStay

      return minStay || undefined
    }
    return undefined
  }

  maybeSetTentativeDate(id: string, date: string) {
    if (
      !this.selectedRange ||
      this.selectedRange.id !== id ||
      !isAfter(parseISODate(date), parseISODate(this.selectedRange.start))
    )
      return

    const {
      blockedDayIsAfterSelectedRangeStart,
      differentDates,
      hasDifferentDates,
      isAllDaysInRangeAvailable,
      isAllDaysWithSingleNightAvailable,
    } = this.getAvailability(id, date)

    if (
      hasDifferentDates &&
      isAllDaysWithSingleNightAvailable &&
      (differentDates.includes(formatDate(date)) || blockedDayIsAfterSelectedRangeStart)
    ) {
      this.tentativeEnd = undefined
    } else if (isAllDaysInRangeAvailable) {
      this.tentativeEnd = date
    }
  }
}
